import type { CatalogProduct } from '../../types'
import type { FacetBase, FacetFieldSource, NumericFacet, OptionFacet } from './types'
import {
  isFilterableFacetKey,
  isDiscreteFacetKey,
  isNumericFacetKey,
  isCheckboxFacetKey,
  normalizeFieldLabel,
  sanitizeOptionValue,
  toLooseNumber,
} from './productFilters.shared'

export const buildSearchFacets = (
  products: CatalogProduct[],
): {
  optionFacets: OptionFacet[]
  numericFacets: NumericFacet[]
} => {
  const optionBuckets = new Map<string, { facet: FacetBase; values: Set<string>; counts: Map<string, number> }>()
  const numericBuckets = new Map<string, { facet: FacetBase; values: number[] }>()

  const registerValue = (source: FacetFieldSource, key: string, value: unknown, seenOptionKeys?: Set<string>) => {
    const normalizedKey = key.trim()
    if (!isFilterableFacetKey(normalizedKey)) return
    if (!normalizedKey) return
    const id = `${source}:${normalizedKey}`
    const facet: FacetBase = {
      id,
      source,
      key: normalizedKey,
      label: normalizeFieldLabel(normalizedKey),
    }

    const numericValue = isDiscreteFacetKey(normalizedKey) || !isNumericFacetKey(normalizedKey) ? null : toLooseNumber(value)
    if (numericValue !== null) {
      const target = numericBuckets.get(id) ?? { facet, values: [] }
      target.values.push(numericValue)
      numericBuckets.set(id, target)
      return
    }

    const optionValue = sanitizeOptionValue(value)
    if (!optionValue) return
    const optionKey = `${id}:${optionValue}`
    if (seenOptionKeys?.has(optionKey)) return
    seenOptionKeys?.add(optionKey)
    const target = optionBuckets.get(id) ?? { facet, values: new Set<string>(), counts: new Map<string, number>() }
    target.values.add(optionValue)
    target.counts.set(optionValue, (target.counts.get(optionValue) ?? 0) + 1)
    optionBuckets.set(id, target)
  }

  products.forEach((product) => {
    const seenOptionKeys = new Set<string>()
    Object.entries(product.customFields ?? {}).forEach(([key, value]) => {
      const entries = Array.isArray(value) ? value : [value]
      entries.forEach((entry) => registerValue('custom', key, entry, seenOptionKeys))
    })
    ;(product.variants ?? []).forEach((variant) => {
      ;[variant.attributes, variant.overrides].forEach((fields) => {
        Object.entries(fields ?? {}).forEach(([key, value]) => {
          const entries = Array.isArray(value) ? value : [value]
          entries.forEach((entry) => registerValue('custom', key, entry, seenOptionKeys))
        })
      })
    })
    ;(product.metadata ?? []).forEach((entry) => {
      registerValue('metadata', entry.key, entry.value, seenOptionKeys)
    })
  })

  const optionFacets: OptionFacet[] = Array.from(optionBuckets.values())
    .map(({ facet, values, counts }) => ({
      ...facet,
      options: Array.from(values)
        .sort((left, right) => isDiscreteFacetKey(facet.key) ? Number(left) - Number(right) : left.localeCompare(right, 'it', { sensitivity: 'base' }))
        .slice(0, 16),
      counts: Object.fromEntries(Array.from(values).map((value) => [value, counts.get(value) ?? 0])),
    }))
    .filter((facet) => facet.options.length > 1 || isCheckboxFacetKey(facet.key))
    .sort((left, right) => left.label.localeCompare(right.label, 'it', { sensitivity: 'base' }))
    .slice(0, 10)

  const numericFacets: NumericFacet[] = Array.from(numericBuckets.values())
    .map(({ facet, values }) => ({
      ...facet,
      min: Math.min(...values),
      max: Math.max(...values),
    }))
    .filter((facet) => Number.isFinite(facet.min) && Number.isFinite(facet.max))
    .sort((left, right) => left.label.localeCompare(right.label, 'it', { sensitivity: 'base' }))
    .slice(0, 10)

  return { optionFacets, numericFacets }
}
